export type InvestigationStatus = 'queued' | 'running' | 'completed' | 'failed';

// Shared with the AI settings contract, so a model the settings can pick is one a run can report.
export type AiProvider = 'anthropic' | 'openai' | 'openrouter';
export type AiEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export interface InvestigationAction {
  title: string;
  detail: string;
  kind: 'resolve' | 'investigate';
  // Indices into result.findings; absent on runs from before evidence links
  basedOn?: number[];
  // The Linear issue staff filed for this action; `status` is set only while it is being created
  linearIssue?: LinearIssueRef | null;
}

export interface LinearIssueRef {
  id?: string;
  identifier?: string | null;
  url?: string;
  status?: 'creating';
}

// One fact the Support Agent established, citing the steps (by index) that show it.
export interface EstablishedFact {
  title: string;
  detail: string;
  evidence: number[];
}

export interface InvestigationResult {
  title?: string;
  // Set when a known fix's confirmation checks all passed and answered the ticket outright
  matchedLearning?: { id: string; title: string } | null;
  summary: string;
  findings?: EstablishedFact[];
  actions: InvestigationAction[];
  confidence: number;
  draftReply: string;
}

export interface InvestigationStep {
  tool: string;
  why?: string;
  // One line naming what was looked up and what came back; absent on runs from before it was recorded
  summary?: string;
  args: unknown;
  resultPreview: string;
  startedAt: string;
  durationMs: number;
  isError: boolean;
}

export type ProposalTool =
  | 'update_account'
  | 'update_account_status'
  | 'update_asset'
  | 'update_entity'
  | 'update_supplier'
  | 'update_task'
  | 'recapture_invoice'
  | 'enable_data_collection';

export type ProposalStatus = 'proposed' | 'applying' | 'applied' | 'dismissed' | 'stale' | 'failed';

export interface ProposalChange {
  field: string;
  before: unknown;
  after: unknown;
}

export interface StaleChange {
  field: string;
  expected: unknown;
  current: unknown;
}

// A write the Support Agent wanted to make. Nothing changes until a staff member applies it.
export interface ProposedAction {
  _id: string;
  tool: ProposalTool;
  targetType: 'account' | 'asset' | 'entity' | 'supplier' | 'task' | 'invoiceCapture' | 'automation';
  targetId: string;
  targetLabel?: string | null;
  summary: string;
  rationale: string;
  args: Record<string, unknown>;
  changes: ProposalChange[];
  status: ProposalStatus;
  createdAt: string;
  decidedBy?: string | null;
  decidedByName?: string | null;
  decidedAt?: string | null;
  error?: string | null;
  staleChanges?: StaleChange[] | null;
  basedOn?: number[];
  // What else the write carries, e.g. a status change's effective date
  details?: { label: string; value: unknown }[];
  dismissReason?: DismissReason | null;
  dismissNote?: string | null;
}

export type DismissReason = 'wrong_diagnosis' | 'already_fixed' | 'not_needed' | 'other';

export interface Investigation {
  _id: string;
  ticketId: string;
  status: InvestigationStatus;
  // Unset until the worker's triage step resolves the tier — any run that fails or
  // stalls before that point has none of these three.
  model?: { provider: AiProvider; id: string };
  effort?: AiEffort;
  maxSteps?: number;
  result?: InvestigationResult | null;
  requestedByName?: string | null;
  learningMatch?: LearningMatch | null;
  draftUsedAt?: string | null;
  debug?: {
    systemPrompt?: string;
    contextSummary?: string;
    toolCatalog?: string[];
    steps?: InvestigationStep[];
    reasoning?: string;
    rawOutput?: string;
    usage?: { inputTokens: number; outputTokens: number; cacheReadTokens?: number; cacheWriteTokens?: number; estimatedCost: number | null };
  } | null;
  error?: string | null;
  proposedActions?: ProposedAction[];
  startedAt?: string | null;
  completedAt?: string | null;
  lastProgressAt?: string | null;
  createdAt: string;
}

export interface InvestigationListResponse {
  data: Investigation[];
}

// 180_000 must match STALE_AFTER_MS in supportTicket.investigate.ts — the API
// auto-expires runs past this on the next investigate POST, so Re-run works.
// Queued counts as well as running: the API expires both, and a run the worker
// never claimed would otherwise poll as live forever with no way to re-run it.
export function isStale(inv: Investigation): boolean {
  return (inv.status === 'running' || inv.status === 'queued') && Date.now() - Date.parse(inv.lastProgressAt ?? inv.createdAt) > 180_000;
}

// The known-fix pre-check the run made before (or instead of) investigating
export interface LearningMatch {
  candidates: { learningId: string; title: string; confidence: number; reasoning: string }[];
  bestLearningId: string | null;
  bestTitle: string | null;
  checks: { tool: string; args: unknown; expectation: string; passed: boolean; reasoning: string; stepIndex: number | null }[];
  shortCircuited: boolean;
}
